import { Spin } from "antd";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

import type { ContractTerminalEntity } from "src/app/features/Contract/types/subTypes/ContractTerminalEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { withReturnTo } from "../../../../core/router/navigation/returnTo";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import { isTheSameByDeepCompare } from "../../../../core/utils/utilFunctions";
import type { ExtendedColumnsType } from "../../../index";
import { displayEnumValue } from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import { SelectableTable } from "../../../Table/components/SelectableTable";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";
import { applyTerminalTypeSelection } from "./ContractTerminalsTableUtils";
import { useLoadSitesTerminalsQuery } from "./serverApi";
import type { LoadSitesTerminalsRequest, TerminalTypeState } from "./types";

export interface ContractTerminalsTableProps extends AntCustomFormField<ContractTerminalEntity[]>, CommonFieldAttributes {
    siteId: string;
    selectedTerminalTypes?: TerminalTypeState;
}

const getColumnsConfig = (t: Function): ExtendedColumnsType<ContractTerminalEntity>[] => [
    {
        title: t("terminal.id"),
        key: "id",
        dataIndex: "id",
    },
    {
        title: t("terminal.terminalId"),
        key: "terminalId",
        dataIndex: "terminalId",
    },
    {
        title: t("terminal.terminalType"),
        key: "terminalType",
        dataIndex: "terminalType",
        render: (value) => displayEnumValue(value, "terminal-type"),
    },
];

export const ContractTerminalsTable: FC<ContractTerminalsTableProps> = ({ value = [], onChange, siteId, selectedTerminalTypes }) => {
    const [queryConfig, setQueryConfig] = useState<LoadSitesTerminalsRequest>({});
    const [selectedValues, setSelectedValues] = useState<ContractTerminalEntity[]>(value);
    const prevTerminalTypeRef = useRef<TerminalTypeState | undefined>(undefined);

    // --- Fetch terminals when siteId changes ---
    useEffect(() => {
        const dataGroup = getAssignedDataGroup();
        if (dataGroup && siteId) {
            setQueryConfig({ siteId, dataGroupCode: dataGroup.code });
        }
    }, [siteId]);

    const { data, isFetching } = useLoadSitesTerminalsQuery(queryConfig, { skip: !queryConfig.siteId });
    const terminalList = data?.items;

    // --- Sync from parent form value (external changes) ---
    useEffect(() => {
        if (!isTheSameByDeepCompare(value, selectedValues)) {
            setSelectedValues(value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // --- Apply terminal-type boolean toggles ---
    useEffect(() => {
        if (!terminalList || !selectedTerminalTypes) return;
        if (Object.values(selectedTerminalTypes).every((v) => v === undefined)) return;

        if (isTheSameByDeepCompare(prevTerminalTypeRef.current, selectedTerminalTypes)) return;

        const newSelection = applyTerminalTypeSelection(
            terminalList as ContractTerminalEntity[],
            selectedTerminalTypes,
            prevTerminalTypeRef.current,
            selectedValues ?? [],
        );

        prevTerminalTypeRef.current = selectedTerminalTypes;

        if (!isTheSameByDeepCompare(newSelection, selectedValues)) {
            setSelectedValues(newSelection);
            onChange?.(newSelection);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [terminalList, selectedTerminalTypes]);

    // --- Propagate selection changes to form ---
    const handleDataChanged = useCallback(
        (rows: ContractTerminalEntity[]) => {
            setSelectedValues(rows);
            onChange?.(rows);
        },
        [onChange],
    );

    // --- Row click navigation ---
    // On the create form there is no persisted contract yet, so navigating away to a terminal would
    // discard the unsaved contract (and this terminal selection). Only enable row navigation on edit.
    const { id: contractId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const onRowClick = useCallback(
        (record: ContractTerminalEntity) => {
            navigate(`${BASE_PATH}/${ResourceName.TERMINALS}/${FORM_ACTION.EDIT}/${record.id}`, withReturnTo(location, "menu.contracts"));
        },
        [navigate, location],
    );

    return (
        <Spin spinning={isFetching}>
            {terminalList && (
                <SelectableTable<ContractTerminalEntity>
                    name="contractTerminals_table"
                    getColumnsConfig={getColumnsConfig}
                    propertyAsRowKey="id"
                    dataSource={terminalList as ContractTerminalEntity[]}
                    tableData={selectedValues ?? value}
                    onDataChanged={handleDataChanged}
                    onRow={
                        contractId
                            ? (record: ContractTerminalEntity) => ({
                                  onClick: () => onRowClick(record),
                              })
                            : undefined
                    }
                />
            )}
        </Spin>
    );
};
